
import { 
  User as FirebaseUser,
  signInWithEmailLink, 
  sendSignInLinkToEmail, 
  isSignInWithEmailLink,
  signOut,
  signInWithPopup
} from 'firebase/auth';
import { auth, googleProvider } from '@/lib/firebase';
import { toast } from '@/lib/toast';
import { profileService } from '@/services/ProfileService';
import { User, actionCodeSettings } from './authTypes';
import { setCookie, deleteCookie } from '@/utils/cookieUtils';

export const loadUserProfile = async (firebaseUser: FirebaseUser): Promise<User> => {
  try {
    const profile = await profileService.getUserProfile(firebaseUser.uid);
    
    if (!profile) {
      console.log("Creating new user profile");
      const userName = firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User';
      
      const newProfile = await profileService.createUserProfile({
        id: firebaseUser.uid,
        email: firebaseUser.email || '',
        name: userName
      });
      
      return {
        id: firebaseUser.uid,
        email: firebaseUser.email || '',
        name: userName,
        profile: newProfile
      };
    } else {
      await profileService.updateLastLogin(firebaseUser.uid);
      
      return {
        id: firebaseUser.uid,
        email: firebaseUser.email || '',
        name: firebaseUser.displayName || profile.name,
        profile
      };
    }
  } catch (error) {
    console.error("Error loading user profile:", error);
    return {
      id: firebaseUser.uid,
      email: firebaseUser.email || '',
      name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
      profile: null
    };
  }
};

export const handleEmailLink = async (setIsLoading: (loading: boolean) => void) => {
  if (isSignInWithEmailLink(auth, window.location.href)) {
    const email = localStorage.getItem('emailForSignIn');
    if (email) {
      setIsLoading(true);
      signInWithEmailLink(auth, email, window.location.href)
        .then(() => {
          localStorage.removeItem('emailForSignIn');
          // Set remember me flag if it was checked
          const rememberMe = localStorage.getItem('rememberMeChoice') === 'true';
          if (rememberMe) {
            localStorage.setItem('rememberMe', 'true');
            localStorage.removeItem('rememberMeChoice');
          }
          // Redirect to home instead of just changing URL state
          window.location.href = '/home';
          toast.success('Successfully logged in!');
        })
        .catch((error) => {
          toast.error('Error signing in: ' + error.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      toast.error('Please provide your email to complete the sign-in process');
    }
  }
};

export const loginWithEmail = async (email: string, remember: boolean, setIsLoading: (loading: boolean) => void) => {
  setIsLoading(true);
  try {
    localStorage.setItem('emailForSignIn', email);
    // Store remember choice in localStorage
    localStorage.setItem('rememberMeChoice', remember.toString());
    
    console.log("Sending login link to:", email);
    console.log("Using redirect URL:", actionCodeSettings.url);
    console.log("Remember me:", remember);
    
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    
    toast.success(`Magic link sent to ${email}`, {
      description: "Check your email for the login link",
    });
  } catch (error: any) {
    console.error("Login error:", error);
    if (error?.code === 'auth/quota-exceeded') {
      toast.error(
        "We're experiencing high traffic. Please try again in a few minutes.",
        { description: 'Our email service is temporarily unavailable.' }
      );
    } else if (error?.code === 'auth/unauthorized-continue-uri') {
      toast.error(
        "Login service configuration issue",
        { description: 'Please contact support. The redirect URL is not authorized.' }
      );
    } else if (error instanceof Error) {
      toast.error('Failed to send magic link: ' + error.message);
    } else {
      toast.error('Failed to send magic link');
    }
  } finally {
    setIsLoading(false);
  }
};

export const loginWithGoogleProvider = async (remember: boolean, setIsLoading: (loading: boolean) => void) => {
  setIsLoading(true);
  try {
    const result = await signInWithPopup(auth, googleProvider);
    
    if (remember) {
      localStorage.setItem('rememberMe', 'true');
    }
    
    toast.success('Successfully logged in with Google!');
    // User will be set by the onAuthStateChanged listener
  } catch (error: any) {
    console.error("Google login error:", error);
    if (error.code === 'auth/popup-closed-by-user') {
      toast.error('Login canceled. Please try again.');
    } else if (error instanceof Error) {
      toast.error('Failed to login with Google: ' + error.message);
    } else {
      toast.error('Failed to login with Google');
    }
  } finally {
    setIsLoading(false);
  }
};

export const logoutUser = () => {
  deleteCookie('uid'); // Clear the remember me cookie
  signOut(auth).then(() => {
    toast.success('Successfully logged out');
  }).catch((error) => {
    toast.error('Error logging out: ' + error.message);
  });
};
